<template>
  <div
    class="notif-browser"
    :class="{ 'notif-browser--compact': compact }"
  >
    <div class="notif-browser-filtres">
      <el-radio-group
        v-model="filtres.state"
        :size="compact ? 'small' : 'default'"
        @change="rechargerDepuisLaPremierePage"
      >
        <el-radio-button value="all">
          Toutes
        </el-radio-button>
        <el-radio-button value="unread">
          Non lues
        </el-radio-button>
        <el-radio-button value="read">
          Lues
        </el-radio-button>
      </el-radio-group>

      <el-select
        v-model="filtres.type_key"
        placeholder="Tous les types"
        clearable
        filterable
        class="notif-browser-type"
        :size="compact ? 'small' : 'default'"
        @change="rechargerDepuisLaPremierePage"
      >
        <el-option
          v-for="type in types"
          :key="type.type_key"
          :label="type.label"
          :value="type.type_key"
        />
      </el-select>

      <el-date-picker
        v-model="periode"
        type="daterange"
        unlink-panels
        range-separator="→"
        start-placeholder="Du"
        end-placeholder="Au"
        value-format="YYYY-MM-DD"
        format="DD/MM/YYYY"
        class="notif-browser-periode"
        :size="compact ? 'small' : 'default'"
        @change="rechargerDepuisLaPremierePage"
      />

      <!-- Le serveur marque toutes les non-lues, sans considérer les filtres :
           le bouton disparaît dès qu'un filtre est posé, plutôt que de promettre
           un périmètre qu'il ne tiendrait pas. -->
      <el-button
        v-if="unreadCount > 0 && !filtreActif"
        text
        :size="compact ? 'small' : 'default'"
        @click="onToutMarquerLu"
      >
        Marquer toutes mes notifications comme lues
      </el-button>
    </div>

    <div
      v-loading="loading"
      class="notif-browser-liste"
    >
      <NotificationItem
        v-for="n in items"
        :key="n.id"
        :notification="n"
        @open="onOuvrir"
        @toggle-read="onBasculer"
      />

      <el-empty
        v-if="!loading && !items.length"
        description="Aucune notification pour ces critères"
      />
    </div>

    <el-pagination
      v-if="total > 0"
      class="notif-browser-pagination"
      :layout="compact ? 'total, prev, pager, next' : 'total, sizes, prev, pager, next'"
      :small="compact"
      :total="total"
      :current-page="page"
      :page-size="perPage"
      :page-sizes="[20, 50, 100]"
      @current-change="onChangerDePage"
      @size-change="onChangerLaTaille"
    />
  </div>
</template>

<script setup>
/**
 * La liste complète des notifications : filtres, pagination, bascule lu/non lu.
 *
 * Le même corps sert à la page `/notifications` et à la fenêtre ouverte depuis
 * la cloche. Les deux doivent montrer exactement la même chose — ce qui a été
 * lu comme ce qui ne l'a pas été — donc le contenu vit ici et non dans l'écran
 * qui l'accueille.
 */
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { fetchNotifications, fetchNotificationPreferences } from '@/api/notifications';
import { notify } from '@/utils/notifications';
import NotificationItem from '@/components/Notifications/NotificationItem.vue';

defineProps({
    // Variante resserrée pour la fenêtre : contrôles en `small`, liste bornée
    // en hauteur et défilante, pagination sans sélecteur de taille.
    compact: { type: Boolean, default: false },
});

// Prévenir l'écran hôte qu'on quitte la page : la fenêtre doit se fermer avant
// que la route change, sinon elle recouvre sa propre destination.
const emit = defineEmits(['navigate']);

const router = useRouter();
const store = useStore();

// Le composant tient sa propre liste : elle est paginée et filtrée, là où le
// store ne porte que les quinze dernières affichées par la cloche. Seul le
// compteur de non-lues est partagé, parce qu'il pilote la pastille.
const items = ref([]);
const types = ref([]);
const loading = ref(false);
const total = ref(0);
const page = ref(1);
const perPage = ref(20);
const periode = ref(null);
const filtres = reactive({ state: 'all', type_key: '' });

const unreadCount = computed(() => store.getters.notifications_unread);
const filtreActif = computed(
    () => filtres.state !== 'all' || !!filtres.type_key || periode.value?.length === 2,
);

// La cloche vit dans la barre de navigation, donc au-dessus de cet écran : ce
// qu'on y marque lu doit se voir ici aussi. Le compteur est le signal commun —
// il bouge à chaque bascule comme à chaque notification reçue par le polling.
watch(unreadCount, () => charger());

const charger = async () => {
    loading.value = true;
    try {
        const params = { state: filtres.state, page: page.value, per_page: perPage.value };
        if (filtres.type_key) params.type_key = filtres.type_key;
        if (periode.value?.length === 2) {
            params.from = periode.value[0];
            params.to = periode.value[1];
        }

        const res = await fetchNotifications(params);
        items.value = res?.data || [];
        total.value = res?.meta?.total || 0;

        // Une page vidée par un changement d'état laisserait un écran vide alors
        // qu'il reste des notifications : on revient à la dernière page qui existe.
        const derniere = res?.meta?.last_page || 1;

        if (!items.value.length && total.value > 0 && page.value > derniere) {
            page.value = derniere;
            await charger();
        }
    } catch (e) {
        notify.error("L'historique des notifications n'a pas pu être chargé");
    } finally {
        loading.value = false;
    }
};

// Le catalogue des types passe par les préférences : c'est le même référentiel,
// déjà ouvert à tout utilisateur interne, là où /notification-types est réservé
// à « manage notifications ».
const chargerLesTypes = async () => {
    try {
        const res = await fetchNotificationPreferences();
        types.value = res?.data || [];
    } catch (e) { /* le filtre par type reste simplement vide */ }
};

const rechargerDepuisLaPremierePage = () => {
    page.value = 1;
    charger();
};

const onChangerDePage = (valeur) => {
    page.value = valeur;
    charger();
};

const onChangerLaTaille = (valeur) => {
    perPage.value = valeur;
    page.value = 1;
    charger();
};

const onOuvrir = async (n) => {
    if (!n.read_at) {
        try { await onBasculer(n); } catch (e) { /* la navigation prime */ }
    }
    const url = n.data?.action_url || n.data?.url;
    if (url) {
        emit('navigate', url);
        router.push(url);
    }
};

const onBasculer = async (n) => {
    try {
        // Les actions du store tiennent le compteur de la cloche à jour ; elles
        // rendent la réponse du serveur, qui fait foi sur l'état de la ligne.
        const res = await store.dispatch(
            n.read_at ? 'notifications/markUnread' : 'notifications/markRead',
            n.id,
        );

        n.read_at = res?.data?.read_at ?? null;
        // Le rechargement — nécessaire quand la ligne sort d'une liste filtrée
        // par état — est déclenché par le watch sur le compteur.
    } catch (e) {
        notify.error("L'état de la notification n'a pas pu être modifié");
    }
};

const onToutMarquerLu = async () => {
    try {
        await store.dispatch('notifications/markAllRead');
    } catch (e) {
        notify.error("Les notifications n'ont pas pu être marquées comme lues");
    }
};

onMounted(() => {
    chargerLesTypes();
    charger();
});
</script>

<style lang="scss" scoped>
.notif-browser-filtres {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.notif-browser-type {
    width: 260px;
}

.notif-browser-periode {
    width: 280px;
}

.notif-browser-liste {
    min-height: 120px;
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 4px;
    background-color: var(--el-bg-color-overlay);
    overflow: hidden;
}

.notif-browser-pagination {
    margin-top: 12px;
    justify-content: flex-end;
}

/* Dans la fenêtre, la liste défile au lieu d'allonger la page : les filtres et
   la pagination restent visibles sans sortir de l'écran. */
.notif-browser--compact {
    .notif-browser-liste {
        max-height: 50vh;
        overflow-y: auto;
    }

    .notif-browser-type {
        width: 200px;
    }

    .notif-browser-periode {
        width: 240px;
    }
}
</style>
